/**
 * Gallery Memory Store - Cache management and performance tracking
 */

export function createGalleryMemoryStore() {
  return {
    preload_cache: new Map(),
    preload_in_progress: new Map(),

    variant_lookup_cache: new Map(),
    cache_access_order: [],
    MAX_CACHE_SIZE: 100,
    cache_hits: 0,
    cache_misses: 0,

    _performanceMetrics: {
      variant_updates: 0,
      image_loads: 0,
      cache_clears: 0,
      last_update: Date.now()
    },

    isImagePreloaded(url, orientation = null) {
      if (!url) return false;
      const cache_key = orientation ? `${orientation}-${url}` : url;
      return this.preload_cache.has(cache_key);
    },

    async preloadImageBatch(urls, orientation = null) {
      if (!urls || urls.length === 0) return;

      const context_orientation = orientation || Alpine.store('galleryCore').orientation;

      const urls_to_load = urls.filter(url => !this.isImagePreloaded(url, context_orientation));

      if (urls_to_load.length === 0) return;

      const load_promises = urls_to_load.map(url => {
        const cache_key = context_orientation ? `${context_orientation}-${url}` : url;

        if (this.preload_in_progress.has(cache_key)) {
          return this.preload_in_progress.get(cache_key);
        }

        const promise = new Promise((resolve) => {
          const img = new Image();
          let resolved = false;

          const complete = () => {
            if (!resolved) {
              resolved = true;
              this.preload_cache.set(cache_key, true);
              this.preload_in_progress.delete(cache_key);
              resolve();
            }
          };

          img.onload = complete;
          img.onerror = complete;
          img.src = url;
        });

        this.preload_in_progress.set(cache_key, promise);
        return promise;
      });

      await Promise.all(load_promises);
    },

    async waitForImages(load_promise) {
      if (load_promise) {
        await load_promise;
      }

      // Use double requestAnimationFrame to ensure paint
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
    },

    updateCacheAccessOrder(key) {
      const index = this.cache_access_order.indexOf(key);
      if (index > -1) {
        this.cache_access_order.splice(index, 1);
      }
      this.cache_access_order.push(key);
    },

    clearOldestCacheEntriesIfNeeded() {
      if (this.variant_lookup_cache.size > this.MAX_CACHE_SIZE) {
        const entriesToDelete = this.variant_lookup_cache.size - this.MAX_CACHE_SIZE + 10;

        for (let i = 0; i < entriesToDelete && i < this.cache_access_order.length; i++) {
          const keyToDelete = this.cache_access_order[i];
          this.variant_lookup_cache.delete(keyToDelete);
        }

        this.cache_access_order = this.cache_access_order.slice(entriesToDelete);
      }

      const newestKey = Array.from(this.variant_lookup_cache.keys()).pop();
      if (newestKey && !this.cache_access_order.includes(newestKey)) {
        this.cache_access_order.push(newestKey);
      }
    },

    invalidateCacheForArtworks(oldIds, newIds) {
      const oldIdArray = oldIds.split(',');
      const newIdArray = newIds.split(',');

      const removedIds = oldIdArray.filter(id => !newIdArray.includes(id));
      const addedIds = newIdArray.filter(id => !oldIdArray.includes(id));
      const changedIds = [...removedIds, ...addedIds];

      if (changedIds.length === 0) return;

      const keysToDelete = [];
      this.variant_lookup_cache.forEach((value, key) => {
        if (changedIds.some(id => key.startsWith(id + '-'))) {
          keysToDelete.push(key);
        }
      });

      keysToDelete.forEach(key => {
        this.variant_lookup_cache.delete(key);
        const index = this.cache_access_order.indexOf(key);
        if (index > -1) {
          this.cache_access_order.splice(index, 1);
        }
      });

      if (keysToDelete.length > 0) {
      }
    },

    getCacheMetrics() {
      const hit_rate = this.cache_hits + this.cache_misses > 0
        ? (this.cache_hits / (this.cache_hits + this.cache_misses) * 100).toFixed(2)
        : 0;

      return {
        size: this.variant_lookup_cache.size,
        max_size: this.MAX_CACHE_SIZE,
        hits: this.cache_hits,
        misses: this.cache_misses,
        hit_rate: `${hit_rate}%`,
        memory_estimate: `${(this.variant_lookup_cache.size * 0.5).toFixed(2)}KB`
      };
    },

    clearCache() {
      this.variant_lookup_cache.clear();
      this.cache_access_order = [];
      this.cache_hits = 0;
      this.cache_misses = 0;
    },

    getPerformanceReport() {
      const cache_metrics = this.getCacheMetrics();
      const now = Date.now();
      const uptime = now - (this._performanceMetrics?.last_update || now);

      return {
        cache: cache_metrics,
        updates: {
          variant_changes: this._performanceMetrics?.variant_updates || 0,
          image_loads: this._performanceMetrics?.image_loads || 0,
          cache_clears: this._performanceMetrics?.cache_clears || 0
        },
        memory: {
          preload_cache_size: this.preload_cache.size,
          variant_cache_size: this.variant_lookup_cache.size,
          estimated_memory: `${(
            (this.preload_cache.size * 2) +
            (this.variant_lookup_cache.size * 0.5)
          ).toFixed(2)}KB`
        },
        performance: {
          uptime_ms: uptime,
          avg_update_rate: uptime > 0
            ? `${((this._performanceMetrics?.variant_updates || 0) / (uptime / 1000)).toFixed(2)}/sec`
            : '0/sec'
        }
      };
    },

    incrementVariantUpdates() {
      this._performanceMetrics.variant_updates++;
    },

    incrementImageLoads(count = 1) {
      this._performanceMetrics.image_loads += count;
    },

    incrementCacheClears() {
      this._performanceMetrics.cache_clears++;
    }
  };
}